/**
 * Resolve human-readable study / site / owner labels for QMS records.
 * API payloads only return UUIDs; UI needs names for identification.
 */

import type { QMSRecord } from "@/types/qms.type";
import { deriveQmsCapaOwnerInitials } from "@/utils/qms/qmsDisplay";

export interface QmsSiteLookup {
  id: string;
  name: string;
  siteNumber?: string | null;
}

export interface QmsUserLookup {
  id: string;
  name: string;
  role?: string;
}

export interface QmsStudyLookup {
  id: string;
  title: string;
  protocolNumber?: string | null;
}

export interface QmsDisplayLookups {
  study?: QmsStudyLookup | null;
  sitesById: Map<string, QmsSiteLookup>;
  usersById: Map<string, QmsUserLookup>;
}

const isUuidLike = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );

const isPlaceholderLabel = (value: string | undefined | null): boolean => {
  if (!value?.trim()) {
    return true;
  }
  const v = value.trim();
  if (v === "Unknown" || v === "Not assigned" || v === "All Sites") {
    return true;
  }
  // Only "Study <uuid>" / "Site <uuid>" are placeholders; real names like "Site 101" are not.
  const prefixed = /^(?:Study|Site)\s+(.+)$/i.exec(v)?.[1];
  if (prefixed && isUuidLike(prefixed)) {
    return true;
  }
  return isUuidLike(v);
};

export const formatQmsSiteLabel = (site: QmsSiteLookup): string => {
  const number = site.siteNumber?.trim();
  const name = site.name?.trim() || "Unnamed site";
  return number ? `${number} — ${name}` : name;
};

export const formatQmsStudyLabel = (study: QmsStudyLookup): string => {
  const title = study.title?.trim() || "Untitled study";
  const protocol = study.protocolNumber?.trim();
  return protocol ? `${title} (${protocol})` : title;
};

/** Apply resolved names onto a QMS record for UI display. */
export const applyQmsDisplayNames = (
  record: QMSRecord,
  lookups: QmsDisplayLookups,
): QMSRecord => {
  const studyLabel = lookups.study
    ? formatQmsStudyLabel(lookups.study)
    : isPlaceholderLabel(record.study)
      ? "—"
      : record.study;

  const siteLookup = record.siteId
    ? lookups.sitesById.get(record.siteId)
    : undefined;
  const siteLabel = siteLookup
    ? formatQmsSiteLabel(siteLookup)
    : record.siteId
      ? isPlaceholderLabel(record.site)
        ? "Unknown site"
        : record.site
      : "Not assigned";

  const ownerLookup = lookups.usersById.get(record.ownerId);
  const ownerName = ownerLookup?.name?.trim();
  const ownerLabel =
    ownerName ||
    (isPlaceholderLabel(record.owner) ? "Unknown owner" : record.owner);
  const ownerInitials = ownerName
    ? deriveQmsCapaOwnerInitials(ownerName)
    : isPlaceholderLabel(record.ownerInitials)
      ? "—"
      : record.ownerInitials;
  const ownerRole =
    ownerLookup?.role?.trim() ||
    (isPlaceholderLabel(record.ownerRole) ? "—" : record.ownerRole);

  const investigatedRaw = record.investigatedBy?.trim() ?? "";
  const investigatedLookup =
    investigatedRaw && lookups.usersById.has(investigatedRaw)
      ? lookups.usersById.get(investigatedRaw)
      : undefined;
  const investigatedBy = investigatedLookup?.name?.trim()
    ? investigatedLookup.name.trim()
    : investigatedRaw && isUuidLike(investigatedRaw)
      ? "Unknown investigator"
      : record.investigatedBy;

  return {
    ...record,
    study: studyLabel,
    site: siteLabel,
    owner: ownerLabel,
    ownerInitials,
    ownerRole,
    investigatedBy,
  };
};

export const applyQmsDisplayNamesToList = (
  records: QMSRecord[],
  lookups: QmsDisplayLookups,
): QMSRecord[] => records.map((r) => applyQmsDisplayNames(r, lookups));
